/**
 * Cart Figurine Image Injection
 * Injects the AI-generated figurine image into cart line items
 * for the Avante theme (v12). Works on both the /cart page and
 * the cart drawer that opens on any page.
 *
 * Avante uses THREE cart-item locations with different ID patterns:
 *   Cart page:   <tr id="CartItem-{N}" class="cart-item">
 *   Cart drawer:  <tr id="CartDrawer-Item-{N}" class="cart-item">
 *   Notification: <div class="cart-item"> (inside #cart-notification)
 *
 * Each contains:
 *   <td class="cart-item__product">
 *     <!-- image wrapper only rendered if product has a Shopify image -->
 *     <div class="cart-item__details">...</div>
 *   </td>
 *
 * Since the Custom 3D Figurine product has no Shopify product image,
 * the theme renders NO image element at all. This script creates and
 * injects one using the _image_url line item property.
 */
(function () {
  'use strict';

  var INJECTED_ATTR = 'data-figurine-img';
  var debounceTimer = null;

  function injectImages() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;

        // Collect figurine items that have _image_url
        var figurineItems = [];
        cart.items.forEach(function (item, index) {
          var url = item.properties && item.properties._image_url;
          if (url) {
            figurineItems.push({ index: index, url: url, title: item.product_title });
          }
        });

        if (!figurineItems.length) return;

        figurineItems.forEach(function (fi) {
          var lineNum = fi.index + 1; // Avante uses 1-based numbering

          // Find ALL matching rows across cart page, drawer, and notification
          var ids = [
            'CartItem-' + lineNum,         // Cart page
            'CartDrawer-Item-' + lineNum   // Cart drawer
          ];

          ids.forEach(function (rowId) {
            var row = document.getElementById(rowId);
            if (!row) return;

            // Skip if we already injected for this row
            if (row.getAttribute(INJECTED_ATTR)) return;

            var td = row.querySelector('.cart-item__product') || row.querySelector('td');
            if (!td) return;

            var details = td.querySelector('.cart-item__details');

            // Check if theme already rendered a product image we can swap
            var existingImg = td.querySelector('img:not(svg img)');
            if (existingImg) {
              // Make sure it's a product image, not a UI icon (spinner, trash, etc.)
              var isUIElement = existingImg.closest('.loading-overlay__spinner') ||
                                existingImg.closest('.quantity') ||
                                existingImg.closest('cart-remove-button') ||
                                existingImg.closest('.remove-button');
              if (!isUIElement) {
                existingImg.src = fi.url;
                existingImg.srcset = '';
                existingImg.alt = fi.title + ' (Custom Design)';
                row.setAttribute(INJECTED_ATTR, '1');
                return;
              }
            }

            // No existing product image — create and inject one
            var wrapper = document.createElement('div');
            wrapper.style.cssText =
              'width:100px;min-width:100px;height:100px;' +
              'flex-shrink:0;margin-right:16px;margin-bottom:12px;' +
              'border-radius:8px;overflow:hidden;background:#f5f5f7;';

            var img = document.createElement('img');
            img.src = fi.url;
            img.alt = fi.title + ' (Custom Design)';
            img.loading = 'lazy';
            img.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;';

            wrapper.appendChild(img);

            // Make the td a flex row so image sits beside details
            td.style.display = 'flex';
            td.style.alignItems = 'flex-start';

            if (details) {
              td.insertBefore(wrapper, details);
            } else {
              td.prepend(wrapper);
            }

            row.setAttribute(INJECTED_ATTR, '1');
          });
        });
      })
      .catch(function () {});
  }

  function debouncedInject() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(injectImages, 300);
  }

  // --- Run on page load ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectImages);
  } else {
    injectImages();
  }

  // --- Watch for cart drawer opening / cart content updating ---
  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      // Check for attribute changes (drawer becoming visible)
      if (m.type === 'attributes' && m.target.id === 'CartDrawer') {
        debouncedInject();
        return;
      }
      // Check for new cart-item nodes being added
      if (m.addedNodes.length) {
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (node.nodeType === 1 && (
            (node.classList && node.classList.contains('cart-item')) ||
            (node.querySelector && node.querySelector('.cart-item')) ||
            (node.id && node.id.indexOf('CartItem') !== -1) ||
            (node.id && node.id.indexOf('CartDrawer') !== -1)
          )) {
            debouncedInject();
            return;
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'open']
  });

  // --- Re-run after fetch-based cart updates ---
  var origFetch = window.fetch;
  window.fetch = function () {
    var result = origFetch.apply(this, arguments);
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    if (url.indexOf('/cart') !== -1) {
      result.then(function () {
        debouncedInject();
      }).catch(function () {});
    }
    return result;
  };

  // --- Re-run after XMLHttpRequest cart updates ---
  var origXHROpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function () {
    var xhrUrl = arguments[1] || '';
    if (typeof xhrUrl === 'string' && xhrUrl.indexOf('/cart') !== -1) {
      this.addEventListener('load', function () {
        debouncedInject();
      });
    }
    return origXHROpen.apply(this, arguments);
  };
})();
